import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Minimize2, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const MaiAIChat = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { type: 'bot', message: "Hi! I'm Mai, your AI assistant. How can I help you today?" }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Parse markdown-style links [text](url) and convert to clickable links
  const parseMessage = (message) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(message)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: message.slice(lastIndex, match.index) });
      }
      // Add the link
      parts.push({ type: 'link', text: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < message.length) {
      parts.push({ type: 'text', content: message.slice(lastIndex) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: message }];
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage = inputMessage.trim();
    setChatMessages(prev => [...prev, { type: 'user', message: userMessage }]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Call the AI backend
      const response = await fetch('http://localhost:5001/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          history: chatMessages.map(msg => ({
            role: msg.type === 'user' ? 'user' : 'assistant',
            content: msg.message
          }))
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from AI service');
      }

      const data = await response.json();
      
      setTimeout(() => {
        setChatMessages(prev => [...prev, { 
          type: 'bot', 
          message: data.response 
        }]);
        setIsTyping(false);
      }, 500); // Small delay for natural feel

    } catch (error) {
      console.error('Error calling AI service:', error);
      
      // Fallback to client-side intelligent responses
      const fallbackResponse = getClientFallback(userMessage);
      
      setTimeout(() => {
        setChatMessages(prev => [...prev, { 
          type: 'bot', 
          message: fallbackResponse 
        }]);
        setIsTyping(false);
      }, 500);
    }
  };

  // Client-side fallback for when AI service is unavailable
  const getClientFallback = (message) => {
    const msgLower = message.toLowerCase();
    
    if (msgLower.includes('sign') || msgLower.includes('register') || msgLower.includes('account')) {
      return "Great! You can sign up for Shiftry here: [Sign Up](/signup). We offer a 7-day free trial with no credit card required!";
    }
    
    if (msgLower.includes('price') || msgLower.includes('cost') || msgLower.includes('plan')) {
      return "Our pricing is very competitive! Basic plan starts at $2.53/user/month, Professional at $3.55/user/month (includes timesheets), and custom Enterprise solutions. [View Pricing](/pricing)";
    }
    
    if (msgLower.includes('feature') || msgLower.includes('what can')) {
      return "Shiftry offers Smart Scheduling, Timesheet Management, Team Communication, Analytics, and AI Automation. [Explore Features](/solutions)";
    }
    
    if (msgLower.includes('document') || msgLower.includes('guide') || msgLower.includes('how to')) {
      return "Check our comprehensive [Documentation](/documentation) or tell me what you're looking for!";
    }
    
    if (msgLower.includes('contact') || msgLower.includes('sales') || msgLower.includes('demo')) {
      return "I'd be happy to connect you! Visit our [Contact Page](/contact) to schedule a demo or speak with our sales team.";
    }
    
    if (msgLower.includes('timesheet') || msgLower.includes('time track')) {
      return "Timesheet & time tracking is available in Professional and Enterprise plans! Includes GPS verification and detailed reports. [View Plans](/pricing)";
    }
    
    return "I'm here to help! Ask me about features, pricing, getting started, or check our [Documentation](/documentation) for detailed guides.";
  };

  return (
    <>
      {/* AI Live Support Chat */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-24 right-6 w-96 bg-white rounded-2xl shadow-2xl overflow-hidden z-50 border-2 border-blue-600"
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white rounded-full p-1">
                  <img 
                    src="/manocore_ai_logo.png" 
                    alt="Mai AI" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Mai - AI Assistant</h3>
                  <p className="text-blue-100 text-xs">Powered by Manocore Technologies</p>
                </div>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="h-96 overflow-y-auto p-4 bg-gray-50">
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`mb-4 flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-900 shadow-md border border-gray-200'
                    }`}
                  >
                    {msg.type === 'bot' && (
                      <div className="flex items-center gap-2 mb-1">
                        <img 
                          src="/manocore_ai_logo.png" 
                          alt="Mai" 
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="text-xs font-semibold text-blue-600">Mai</span>
                      </div>
                    )}
                    <p className="text-sm">
                      {parseMessage(msg.message).map((part, i) => {
                        if (part.type === 'link') {
                          return (
                            <span
                              key={i}
                              onClick={() => {
                                navigate(part.url);
                                setIsChatOpen(false);
                              }}
                              className="text-blue-600 hover:text-blue-800 underline cursor-pointer font-semibold"
                            >
                              {part.text}
                            </span>
                          );
                        }
                        return <span key={i}>{part.content}</span>;
                      })}
                    </p>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="mb-4 flex justify-start">
                  <div className="bg-white text-gray-900 shadow-md border border-gray-200 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <img 
                        src="/manocore_ai_logo.png" 
                        alt="Mai" 
                        className="w-5 h-5 rounded-full"
                      />
                      <Loader className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm text-gray-600">Mai is typing...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Chat Input */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Ask me anything..."
                  className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-600 focus:outline-none"
                />
                <button
                  onClick={handleSendMessage}
                  className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 transition-all"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* AI Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsChatOpen(!isChatOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full shadow-2xl flex items-center justify-center z-50 hover:shadow-3xl transition-all"
      >
        {isChatOpen ? (
          <Minimize2 className="w-7 h-7" />
        ) : (
          <div className="relative">
            <MessageCircle className="w-7 h-7" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
          </div>
        )}
      </motion.button>
    </>
  );
};

export default MaiAIChat;
